import { useEffect, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { navLinks, siteInfo } from "@/mocks/site";
import { categories } from "@/mocks/categories";
import SearchBox from "@/components/base/SearchBox";

const linkBase = "whitespace-nowrap text-sm transition-colors";

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200/70 bg-background-50/95 backdrop-blur">
      <div className="hidden border-b border-background-200/70 bg-foreground-950 text-background-100 md:block">
        <div className="mx-auto flex h-9 max-w-[1280px] items-center justify-between px-4 text-xs md:px-6">
          <p className="flex items-center gap-2">
            <i className="ri-fire-line text-accent-400" />
            已收录词条 <strong className="font-semibold text-accent-300">12,486</strong> 条，热门黑料词每日更新
          </p>
          <p className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <i className="ri-mail-line" />
              {siteInfo.email}
            </span>
          </p>
        </div>
      </div>

      <div className="mx-auto flex h-16 max-w-[1280px] items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-search-eye-line text-lg" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold tracking-tight text-foreground-950">
              {siteInfo.name}
            </span>
            <span className="mt-0.5 text-[10px] tracking-[0.2em] text-foreground-400">{siteInfo.shortName} · INDEX</span>
          </span>
        </Link>

        <nav aria-label="主导航" className="hidden items-center gap-6 md:flex">
          {navLinks.slice(0, 2).map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? "font-medium text-primary-600" : "text-foreground-700 hover:text-primary-600"}`
              }
            >
              {link.label}
            </NavLink>
          ))}

          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setCategoryOpen((prev) => !prev)}
              aria-expanded={categoryOpen}
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm text-foreground-700 transition-colors hover:text-primary-600"
            >
              主题分类
              <i className={`ri-arrow-down-s-line transition-transform ${categoryOpen ? "rotate-180" : ""}`} />
            </button>
            {categoryOpen && (
              <div className="absolute left-0 top-full z-50 mt-3 w-72 rounded-lg border border-background-200 bg-background-50 p-2">
                {categories.map((category) => (
                  <Link
                    key={category.slug}
                    to={`/category/${category.slug}`}
                    onClick={() => setCategoryOpen(false)}
                    className="flex items-start gap-3 rounded-md p-2.5 transition-colors hover:bg-background-100"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                      <i className={`${category.icon} text-base`} />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-foreground-950">{category.name}</span>
                      <span className="mt-0.5 block text-xs text-foreground-500">{category.count} 条汇总</span>
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {navLinks.slice(2).map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? "font-medium text-primary-600" : "text-foreground-700 hover:text-primary-600"}`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto hidden w-full max-w-xs lg:block">
          <SearchBox variant="compact" placeholder="搜索关键词…" />
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-expanded={menuOpen}
          aria-label="打开导航菜单"
          className="ml-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-800 md:hidden"
        >
          <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200/70 bg-background-50 px-4 py-4 md:hidden">
          <SearchBox variant="compact" placeholder="搜索关键词…" />
          <nav aria-label="移动端导航" className="mt-4 flex flex-col">
            {navLinks.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                onClick={() => setMenuOpen(false)}
                className="border-b border-background-200/70 py-3 text-sm text-foreground-800"
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
          <p className="mt-4 text-xs font-medium tracking-wide text-foreground-400">主题分类</p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {categories.map((category) => (
              <Link
                key={category.slug}
                to={`/category/${category.slug}`}
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 rounded-md bg-background-100 px-3 py-2 text-sm text-foreground-800"
              >
                <i className={`${category.icon} text-base text-primary-600`} />
                {category.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}